/**
 * Builder section 5 — the rates, one row per audience × ad unit, straight
 * from the quote's `rates`. A row the seller hasn't touched shows the
 * server's starting rate and refreshes with every quote; a rate the seller
 * types (or takes from "Why this rate?") is theirs until they reset it. Any
 * rate the pricing policy flags needs an internal reason before Create.
 */

import React, { useState } from 'react';
import { Button } from '../../../ui';
import type { GuardrailFinding, MediaPlanQuote, MediaPlanUnitDef, MediaPlanUnitKey, PricingRateRow, UpchargesMode } from '../types';
import {
  REASON_MAX_CHARS,
  REASON_MIN_CHARS,
  fieldId,
  parseRate,
  rateField,
  reasonDirection,
  reasonKey,
  segmentIdsByKey,
  type PlanDraft,
  type RateEdit,
  type ReasonDirection,
} from '../planDraft';
import { reasonField } from '../planChecks';
import { HISTORY_FLAG_WORDS, RATE_BASIS_LABELS, historyReferenceNote } from '../vocab';
import { fieldStyle, fmtDate, fmtInt, fmtUsd, hintStyle, labelStyle, mutedText, numberFieldStyle } from '../formStyles';
import FormSection from './FormSection';
import { FieldError, fieldAria, type ErrorFor } from './fields';

interface Props {
  draft: PlanDraft;
  /** The last quote (it may be for an earlier draft while a new one is on its way). */
  quote: MediaPlanQuote | null;
  /** True while the shown quote is for an earlier draft. */
  updating: boolean;
  units: MediaPlanUnitDef[];
  /** The budget option whose impressions and cost each row shows. */
  tierIndex: number;
  onRateChange: (segmentId: string, unitKey: MediaPlanUnitKey, edit: RateEdit | null) => void;
  onReasonChange: (key: string, text: string) => void;
  onUpchargesModeChange: (mode: UpchargesMode) => void;
  errorFor: ErrorFor;
  disabled?: boolean;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;
const targetingCost = (row: PricingRateRow): number => round2(row.upcharges.reduce((sum, u) => sum + u.cpmAdd, 0));

/** A net CPM as the rate box shows it: all-in, or the base when targeting goes on top. */
export function boxValueForNet(row: PricingRateRow, netCpm: number, mode: UpchargesMode): string {
  const base = mode === 'card' ? round2(netCpm - targetingCost(row)) : netCpm;
  return (base > 0 ? base : netCpm).toFixed(2);
}

/** The starting rate in the box for a row with no rate of the seller's. */
export const startingBoxValue = (row: PricingRateRow, mode: UpchargesMode): string => (row.effective ? boxValueForNet(row, row.effective.netCpm, mode) : '');

interface RowView {
  row: PricingRateRow;
  unit: MediaPlanUnitDef | undefined;
  segmentId: string | undefined;
  edit: RateEdit | undefined;
  findings: GuardrailFinding[];
  prior: number | undefined;
  onPlan: { impressions: number; costUsd: number } | null;
}

/** Quote rows grouped by audience, matched to the draft's audiences (by key, else by position while a rename re-prices). */
function rowViews(draft: PlanDraft, quote: MediaPlanQuote, units: MediaPlanUnitDef[], tierIndex: number): Array<{ segmentKey: string; label: string; rows: RowView[] }> {
  const idsByKey = segmentIdsByKey(draft.segments);
  const quotedKeys = (quote.request.segments ?? []).map((s) => s.key);
  const idFor = (segmentKey: string): string | undefined => {
    if (idsByKey.has(segmentKey)) return idsByKey.get(segmentKey);
    const i = quotedKeys.indexOf(segmentKey);
    if (i >= 0) return draft.segments[i]?.id;
    return segmentKey === 'all' && draft.segments.length === 1 ? draft.segments[0].id : undefined;
  };
  const tier = quote.preview.tiers[Math.min(tierIndex, quote.preview.tiers.length - 1)];
  const groups: Array<{ segmentKey: string; label: string; rows: RowView[] }> = [];
  for (const row of quote.rates ?? []) {
    let group = groups.find((g) => g.segmentKey === row.segmentKey);
    const segmentId = idFor(row.segmentKey);
    if (!group) {
      const segment = draft.segments.find((s) => s.id === segmentId);
      const label = segment?.label.trim() || quote.request.segments?.find((s) => s.key === row.segmentKey)?.label || 'Audience';
      group = { segmentKey: row.segmentKey, label, rows: [] };
      groups.push(group);
    }
    const lines = (tier?.lines ?? []).filter((l) => l.kind === 'paid' && l.unitKey === row.unitKey && (l.segmentKey ?? 'all') === row.segmentKey);
    group.rows.push({
      row,
      unit: units.find((u) => u.key === row.unitKey),
      segmentId,
      edit: segmentId ? draft.segments.find((s) => s.id === segmentId)?.rates[row.unitKey] : undefined,
      findings: (quote.guardrails ?? []).filter((f) => f.segmentKey === row.segmentKey && f.unitKey === row.unitKey),
      prior: segmentId ? draft.prior?.rates[segmentId]?.[row.unitKey] : undefined,
      onPlan: lines.length ? { impressions: lines.reduce((s, l) => s + l.impressions, 0), costUsd: lines.reduce((s, l) => s + l.costUsd, 0) } : null,
    });
  }
  return groups;
}

const RatesSection: React.FC<Props> = ({ draft, quote, updating, units, tierIndex, onRateChange, onReasonChange, onUpchargesModeChange, errorFor, disabled = false }) => {
  const groups = quote ? rowViews(draft, quote, units, tierIndex) : [];
  const hasTargetingCost = groups.some((g) => g.rows.some((r) => r.row.rateType === 'cpm' && r.row.upcharges.length > 0));

  return (
    <FormSection
      id="mp-section-rates"
      title="Rates"
      note="Net CPMs. Each starts from what the advertiser has paid when their history is solid, otherwise from list — type your own to change it."
      busy={updating && groups.length > 0}
    >
      {groups.length === 0 ? (
        <div style={mutedText}>Rates show here once the plan is priced.</div>
      ) : (
        <>
          {hasTargetingCost && <TargetingCostControl mode={draft.upchargesMode} onChange={onUpchargesModeChange} disabled={disabled} />}
          {groups.map((g) => (
            <div key={g.segmentKey} role="group" aria-label={groups.length > 1 ? `${g.label} rates` : 'Rates'} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {groups.length > 1 && <div style={styles.groupTitle}>{g.label}</div>}
              {g.rows.map((v) => (
                <RateRow
                  key={`${g.segmentKey}:${v.row.unitKey}`}
                  view={v}
                  audience={groups.length > 1 ? g.label : undefined}
                  draft={draft}
                  quote={quote!}
                  onRateChange={onRateChange}
                  onReasonChange={onReasonChange}
                  errorFor={errorFor}
                  disabled={disabled}
                />
              ))}
            </div>
          ))}
        </>
      )}
    </FormSection>
  );
};

const TargetingCostControl: React.FC<{ mode: UpchargesMode; onChange: (mode: UpchargesMode) => void; disabled: boolean }> = ({ mode, onChange, disabled }) => {
  const options: Array<{ value: UpchargesMode; label: string }> = [
    { value: 'none', label: 'Included in your rate' },
    { value: 'card', label: 'Added on top' },
  ];
  const move = (e: React.KeyboardEvent) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    onChange(mode === 'none' ? 'card' : 'none');
  };
  return (
    <div>
      <div id="mp-targeting-cost-label" style={labelStyle}>
        Targeting cost on your rates
      </div>
      <div role="radiogroup" aria-labelledby="mp-targeting-cost-label" aria-describedby="mp-targeting-cost-hint" style={styles.segmented} onKeyDown={move}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={mode === o.value}
            tabIndex={mode === o.value ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            style={{ ...styles.segment, ...(mode === o.value ? styles.segmentOn : null) }}
          >
            {o.label}
          </button>
        ))}
      </div>
      <div id="mp-targeting-cost-hint" style={hintStyle}>
        For rates you type. History rates already include targeting; list prices add it on top.
      </div>
    </div>
  );
};

interface RowProps {
  view: RowView;
  audience?: string;
  draft: PlanDraft;
  quote: MediaPlanQuote;
  onRateChange: (segmentId: string, unitKey: MediaPlanUnitKey, edit: RateEdit | null) => void;
  onReasonChange: (key: string, text: string) => void;
  errorFor: ErrorFor;
  disabled: boolean;
}

const RateRow: React.FC<RowProps> = ({ view, audience, draft, quote, onRateChange, onReasonChange, errorFor, disabled }) => {
  const { row, unit, segmentId, edit, findings, prior, onPlan } = view;
  const [whyOpen, setWhyOpen] = useState(false);
  const unitName = unit?.label || row.unitKey;
  const mode = draft.upchargesMode;
  const field = segmentId ? rateField(segmentId, row.unitKey) : null;
  const inputId = field ? fieldId(field) : undefined;
  const error = field ? errorFor(field) : undefined;
  const whyId = inputId ? `${inputId}-why` : undefined;
  const setRate = (value: string, origin: RateEdit['origin']) => segmentId && onRateChange(segmentId, row.unitKey, { value, origin });
  const reset = () => segmentId && onRateChange(segmentId, row.unitKey, null);

  if (row.rateType === 'flat') {
    return (
      <div style={styles.row}>
        <div style={styles.unitCell}>
          <div style={styles.unitName}>{unitName}</div>
          <div style={mutedText}>Flat fee</div>
        </div>
        <div style={styles.basisCell}>
          {typeof row.list?.flatUsd === 'number' ? <strong>{fmtUsd(row.list.flatUsd, 0)} flat</strong> : <span style={mutedText}>No price on the rate card</span>}
          <div style={mutedText}>From the rate card · costed before the CPM lines</div>
        </div>
        <OnPlan onPlan={onPlan} />
      </div>
    );
  }

  const value = edit ? edit.value : startingBoxValue(row, mode);
  const net = row.effective?.netCpm;
  const typedRate = edit ? parseRate(edit.value) : null;
  // The box no longer matches what was quoted: the numbers beside it are still catching up.
  const pending =
    !!edit && (typedRate === null || !row.effective || row.effective.basis !== 'seller' || Math.abs(Number(boxValueForNet(row, row.effective.netCpm, mode)) - typedRate) >= 0.005);
  const priorDiffers = typeof prior === 'number' && typeof net === 'number' && Math.abs(prior - net) >= 0.005 && edit?.origin !== 'prior';
  const priorVersion = draft.prior?.version;
  const cost = targetingCost(row);
  // In "Added on top" the box holds the rate before targeting, so the line spells out the net.
  const targetingWords =
    cost > 0 && typeof net === 'number'
      ? mode === 'card'
        ? `+ ${fmtUsd(cost)} targeting = ${fmtUsd(net)} net`
        : row.effective?.foldUpcharges
          ? 'targeting included'
          : `incl. ${fmtUsd(cost)} targeting`
      : null;
  const listWords = typeof row.vsList === 'number' ? vsListWords(row.vsList) : null;
  const detail = [targetingWords, listWords].filter(Boolean).join(' · ');

  let basis: React.ReactNode;
  if (edit?.origin === 'prior') basis = `From v${priorVersion ?? 1}`;
  else if (edit) basis = RATE_BASIS_LABELS.seller;
  else if (!row.effective) basis = <span style={{ color: 'var(--warning)' }}>No list price — type a rate</span>;
  else if (row.effective.basis === 'history' && row.history) basis = `${RATE_BASIS_LABELS.history} · ${row.history.months} mo`;
  else basis = RATE_BASIS_LABELS[row.effective.basis];

  const directions = Array.from(new Set(findings.map((f) => reasonDirection(f.kind)))) as ReasonDirection[];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={styles.row}>
        <div style={styles.unitCell}>
          <div style={styles.unitName}>{unitName}</div>
          {unit?.platformLabel && <div style={mutedText}>{unit.platformLabel}</div>}
        </div>
        <div style={styles.rateCell}>
          <div style={{ position: 'relative' }}>
            <span style={styles.dollar}>$</span>
            <input
              id={inputId}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              aria-label={`${unitName}${audience ? `, ${audience}` : ''}: ${mode === 'card' ? 'rate before targeting' : 'net rate'}, CPM in dollars`}
              style={{ ...numberFieldStyle, paddingLeft: 22, fontWeight: edit ? 700 : 400 }}
              value={value}
              disabled={disabled || !segmentId}
              onChange={(e) => setRate(e.target.value, 'typed')}
              onBlur={() => {
                if (edit && !edit.value.trim()) reset();
              }}
              {...(field ? fieldAria(field, error) : {})}
            />
          </div>
          {field && <FieldError field={field} message={error} />}
        </div>
        <div style={styles.basisCell}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13, color: 'var(--fg-2)' }}>
            <span>{basis}</span>
            {edit && (
              <Button size="sm" variant="ghost" onClick={reset} disabled={disabled} aria-label={`Reset the ${unitName} rate${audience ? ` for ${audience}` : ''} to the starting rate`}>
                Reset
              </Button>
            )}
          </div>
          {!pending && detail && <div style={mutedText}>{detail}</div>}
          {priorDiffers && segmentId && (
            <div style={{ ...mutedText, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>
                v{priorVersion ?? 1} was {fmtUsd(prior!)}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setRate(boxValueForNet(row, prior!, mode), 'adopted')} disabled={disabled}>
                Use {fmtUsd(prior!)}
              </Button>
            </div>
          )}
          <button type="button" aria-expanded={whyOpen} aria-controls={whyId} onClick={() => setWhyOpen((o) => !o)} style={styles.whyToggle}>
            {whyOpen ? '▾' : '▸'} Why this rate?
          </button>
        </div>
        <OnPlan onPlan={onPlan} />
      </div>

      {whyOpen && (
        <div id={whyId}>
          <WhyThisRate row={row} quote={quote} currentNet={net} onUse={(netCpm) => setRate(boxValueForNet(row, netCpm, mode), 'adopted')} disabled={disabled || !segmentId} />
        </div>
      )}

      {segmentId &&
        directions.map((direction) => {
          const key = reasonKey(direction, segmentId, row.unitKey);
          const id = fieldId(reasonField(key));
          const text = draft.reasons[key] ?? '';
          const length = text.trim().length;
          return (
            <div key={direction} role="group" aria-labelledby={`${id}-label`} style={styles.callout}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>
                {findings
                  .filter((f) => reasonDirection(f.kind) === direction)
                  .map((f) => (
                    <div key={f.id}>{f.message}</div>
                  ))}
              </div>
              <label id={`${id}-label`} htmlFor={id} style={labelStyle}>
                Reason (internal — never on the client files)
              </label>
              <textarea
                id={id}
                style={{ ...fieldStyle, minHeight: 76, resize: 'vertical' }}
                value={text}
                maxLength={REASON_MAX_CHARS}
                placeholder={direction === 'above' ? 'Why this rate is right (or fix the rate if it was a typo)…' : 'e.g. Matches their FY27 upfront deal; approved by our sales lead'}
                disabled={disabled}
                aria-describedby={`${id}-count`}
                onChange={(e) => onReasonChange(key, e.target.value)}
              />
              <div id={`${id}-count`} style={{ ...hintStyle, marginTop: 0, color: length > 0 && length < REASON_MIN_CHARS ? 'var(--danger)' : 'var(--fg-4)' }}>
                {length < REASON_MIN_CHARS ? `At least ${REASON_MIN_CHARS} characters · ` : ''}
                {length}/{REASON_MAX_CHARS}
              </div>
            </div>
          );
        })}
    </div>
  );
};

/** "12% below list" / "8% above list"; null when it rounds to 0%. */
export const vsListWords = (ratio: number): string | null => {
  const pct = Math.round(Math.abs(ratio) * 100);
  if (pct === 0) return null;
  return `${pct}% ${ratio < 0 ? 'below' : 'above'} list`;
};

const OnPlan: React.FC<{ onPlan: RowView['onPlan'] }> = ({ onPlan }) => (
  <div style={styles.onPlanCell}>
    {onPlan ? (
      <>
        <div style={{ fontWeight: 600, color: 'var(--fg-1)', fontVariantNumeric: 'tabular-nums' }}>{fmtUsd(onPlan.costUsd, 0)}</div>
        <div style={mutedText}>{fmtInt(onPlan.impressions)} impr.</div>
      </>
    ) : (
      <span style={mutedText}>—</span>
    )}
  </div>
);

interface WhyProps {
  row: PricingRateRow;
  quote: MediaPlanQuote;
  currentNet: number | undefined;
  onUse: (netCpm: number) => void;
  disabled: boolean;
}

const fmtMonth = (ym: string): string => fmtDate(`${ym}-01`).replace(/ \d+,/, '');

/** What stands behind a rate: what they've paid, list, the negotiated card, the category, their past contracts. */
export const WhyThisRate: React.FC<WhyProps> = ({ row, quote, currentNet, onUse, disabled }) => {
  const h = row.history;
  const differs = (n: number | undefined) => typeof n === 'number' && (typeof currentNet !== 'number' || Math.abs(n - currentNet) >= 0.005);
  const category = quote.history?.category;
  const contracted = (quote.history?.contracted ?? []).slice(-3).reverse();
  const listCpm = row.list?.cpm;
  const cost = targetingCost(row);
  return (
    <div style={styles.why}>
      <div style={styles.whyItem}>
        <div style={styles.whyLabel}>What they've paid</div>
        {h ? (
          <div style={styles.whyBody}>
            <strong>{fmtUsd(h.ecpm)}</strong> · {fmtInt(h.paidImpressions)} paid impressions · {h.months} mo
            <div style={mutedText}>
              {historyReferenceNote(h)}
              {h.flags.length > 0 && ` · ${h.flags.map((f) => HISTORY_FLAG_WORDS[f]).join(', ')}`}
            </div>
            {h.applyable && differs(h.ecpm) && (
              <Button size="sm" variant="subtle" style={{ marginTop: 6 }} onClick={() => onUse(h.ecpm)} disabled={disabled}>
                Use {fmtUsd(h.ecpm)}
              </Button>
            )}
          </div>
        ) : (
          <div style={styles.whyBody}>No paid history for this ad unit.</div>
        )}
      </div>
      <div style={styles.whyItem}>
        <div style={styles.whyLabel}>{row.list?.source.scope === 'advertiser' ? 'Negotiated rate' : 'List'}</div>
        {typeof listCpm === 'number' ? (
          <div style={styles.whyBody}>
            <strong>{fmtUsd(typeof row.listNetCpm === 'number' ? row.listNetCpm : listCpm)}</strong>
            {cost > 0 && ` (${fmtUsd(listCpm)} + ${fmtUsd(cost)} targeting)`}
            <div style={mutedText}>{row.list!.source.cardName}</div>
            {differs(row.listNetCpm ?? listCpm) && (
              <Button size="sm" variant="subtle" style={{ marginTop: 6 }} onClick={() => onUse(row.listNetCpm ?? listCpm)} disabled={disabled}>
                Use {fmtUsd(row.listNetCpm ?? listCpm)}
              </Button>
            )}
          </div>
        ) : (
          <div style={styles.whyBody}>No list price for this unit.</div>
        )}
      </div>
      {category && (
        <div style={styles.whyItem}>
          <div style={styles.whyLabel}>{category.label} category</div>
          <div style={styles.whyBody}>
            {fmtUsd(category.weightedEcpm)} average across {category.advertisers} advertiser{category.advertisers === 1 ? '' : 's'}
            <div style={mutedText}>All ad units blended — context, not a rate.</div>
          </div>
        </div>
      )}
      {contracted.length > 0 && (
        <div style={styles.whyItem}>
          <div style={styles.whyLabel}>Their past contracts</div>
          <div style={styles.whyBody}>
            {contracted.map((c) => (
              <div key={`${c.month}:${c.label}`}>
                {fmtMonth(c.month)} · {c.label}
                {typeof c.cpm === 'number' ? ` · ${fmtUsd(c.cpm)} CPM` : ''}
                {typeof c.flatRate === 'number' ? ` · ${fmtUsd(c.flatRate, 0)} flat` : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  groupTitle: { fontSize: 13, fontWeight: 700, color: 'var(--fg-1)', paddingTop: 4 },
  row: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 12, padding: '10px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' },
  unitCell: { flex: '1 1 150px', minWidth: 0 },
  unitName: { fontSize: 13.5, fontWeight: 600, color: 'var(--fg-1)' },
  rateCell: { flex: '0 0 120px' },
  basisCell: { flex: '2 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' },
  // marginLeft keeps it at the right edge when a narrow row wraps it onto its own line.
  onPlanCell: { flex: '0 0 110px', marginLeft: 'auto', textAlign: 'right', fontSize: 13 },
  dollar: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--fg-4)', pointerEvents: 'none' },
  whyToggle: { padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--fg-link)' },
  why: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, padding: 12, borderRadius: 'var(--radius-md)', background: 'var(--bg-surface-2)' },
  whyItem: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  whyLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-4)' },
  whyBody: { fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 },
  callout: { display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--warning-soft)', border: '1px solid var(--warning)' },
  segmented: { display: 'inline-flex', padding: 2, borderRadius: 'var(--radius-md)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-1)' },
  segment: { padding: '6px 12px', border: 'none', borderRadius: 'var(--radius-sm)', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--fg-3)' },
  segmentOn: { background: 'var(--bg-surface-1)', color: 'var(--fg-1)', boxShadow: 'var(--shadow-xs)' },
};

export default RatesSection;
